import {
  BannerListParams,
  BannerView,
  BannerWriteData,
  ContentPagedResponse,
} from "@/types/content";
import { adminUrl } from "../../utils/url";
import { HttpClient } from "../HttpClient";
import { getErrorMessage } from "../errors";
import { IBannersService } from "./IBannersService";
import { adaptBanner, adaptPagedContent } from "./adapters";
import { isAbortError } from "../errors/isAbortError";
import { CONTENT_PAGE_SIZE, buildContentQuery } from "./query";

/**
 * Banners shown on the client app's pages.
 *
 * The whole resource lives under `/api/admin`, reads included — unlike
 * reference data, there is no non-admin mirror — so this opens a single
 * client on `adminUrl`.
 */
export class BannersService implements IBannersService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient({
      baseURL: adminUrl,
      timeout: 10000,
      headers: {},
    });
  }

  async list(
    params: BannerListParams = {},
  ): Promise<ContentPagedResponse<BannerView>> {
    const query = buildContentQuery(params, { pageType: params.pageType });

    try {
      const response = await this.httpClient.get<unknown>(
        `/content/banners?${query.toString()}`,
        params.signal ? { signal: params.signal } : {},
      );

      return adaptPagedContent(
        response,
        adaptBanner,
        params.size ?? CONTENT_PAGE_SIZE,
      );
    } catch (error) {
      if (isAbortError(error)) throw error;

      throw new Error(
        getErrorMessage(
          error,
          "Não foi possível carregar os banners. Atualize a página e tente novamente.",
        ),
      );
    }
  }

  async getById(id: number, signal?: AbortSignal): Promise<BannerView> {
    try {
      const response = await this.httpClient.get<unknown>(
        `/content/banners/${id}`,
        signal ? { signal } : {},
      );

      const banner = adaptBanner(response);
      if (!banner) {
        throw new Error(
          "Não foi possível ler o banner devolvido pelo servidor.",
        );
      }

      return banner;
    } catch (error) {
      if (isAbortError(error)) throw error;

      throw new Error(
        getErrorMessage(
          error,
          "Não foi possível carregar o banner. Atualize a página e tente novamente.",
        ),
      );
    }
  }

  async create(data: BannerWriteData): Promise<number> {
    try {
      return await this.httpClient.post<number>(
        `/content/banners`,
        data,
        {},
        true,
      );
    } catch (error) {
      throw new Error(
        getErrorMessage(
          error,
          "Não foi possível criar o banner. Confirme a imagem e o tipo de página.",
        ),
      );
    }
  }

  async update(id: number, data: BannerWriteData): Promise<void> {
    try {
      await this.httpClient.put<void>(`/content/banners/${id}`, data);
    } catch (error) {
      throw new Error(
        getErrorMessage(
          error,
          "Não foi possível atualizar o banner. Tente guardar novamente.",
        ),
      );
    }
  }

  async delete(id: number): Promise<void> {
    try {
      await this.httpClient.delete<void>(`/content/banners/${id}`);
    } catch (error) {
      throw new Error(
        getErrorMessage(
          error,
          "Não foi possível eliminar o banner. Tente novamente.",
        ),
      );
    }
  }
}
